import { useCallback } from "react";

import { useRouteParams } from "./platform/params";

export type RouteParamValue =
  | string
  | number
  | boolean
  | readonly string[]
  | null
  | undefined;
export type RouteParamsShape = Record<string, RouteParamValue>;

export type SetParamsOptions = {
  /** Web only, default "push". Native always sets params in place. */
  webBehavior?: "push" | "replace";
};

export type ParamHooks<T extends RouteParamsShape> = {
  useParam: <K extends Extract<keyof T, string>>(
    name: K,
  ) => readonly [
    T[K] | undefined,
    (value: T[K] | undefined, options?: SetParamsOptions) => void,
  ];
  useParams: () => {
    params: T;
    /**
     * Web: a shallow push or replace of the query, with keys set to null or
     * undefined removed. Native: merged into the calling screen's route params
     * as given.
     */
    setParams(value: Partial<T>, options?: SetParamsOptions): void;
  };
};

type RawParamHooks = {
  useParam: (
    name: string,
  ) => readonly [
    unknown,
    (value: RouteParamValue, options?: SetParamsOptions) => void,
  ];
  useParams: typeof useRouteParams;
};

function useRouteParam(name: string) {
  const { params, setParams } = useRouteParams();
  const setParam = useCallback(
    (value: RouteParamValue, options?: SetParamsOptions) =>
      setParams({ [name]: value }, options),
    [setParams, name],
  );
  return [params[name], setParam] as const;
}

/**
 * Web values come from the URL query, so they are strings or string arrays.
 * Native values are the route's params, and values a screen wrote with
 * `setParams` keep their JS type. `T` is the screen's declaration and is
 * trusted, not validated.
 */
export function createParam<T extends RouteParamsShape>(): ParamHooks<T>;
export function createParam(): RawParamHooks {
  return {
    useParams: useRouteParams,
    useParam: useRouteParam,
  };
}
